/**
 * CPV-MT-01 F1 · the header location selector: Site ▸ Plant, several at once, "All plants" when the person may read more
 * than one. It lists only the plants the person may read (ScopeContext, from /bp/org/tree/). The choice is saved (the
 * scope preference) when the list closes, and every CPV screen reads it. A person with one plant sees its name, as before.
 *
 * The plant is named here only — never again in a page's header.
 */
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { TreeSelect, Tooltip } from 'antd';
import { Info, MapPin } from 'lucide-react';
import { toast } from 'react-toastify';
import { ALL_PLANTS, useScope } from '@/contexts/ScopeContext';

const ALL_KEY = 'all';
const siteKey = (id) => `s:${id}`;
const plantKey = (id) => `p:${id}`;
const UNASSIGNED_KEY = 's:none';

/** The tree the selector shows: All plants ▸ Site ▸ Plant. A site with one plant is one line (Q4d): the plant, named
 * "Site · Plant" when the two names differ, so a site and its only plant never show twice under the same name. */
function treeDataOf(tree, readable) {
  const node = (p, title = p.name) => ({ title, value: plantKey(p.id), key: plantKey(p.id), plantId: p.id,
    label: p.name, 'data-testid': `scope-plant-${p.id}` });
  const sites = (tree?.sites || []).filter((s) => (s.plants || []).length).map((s) => {
    if (s.plants.length === 1) {
      const [only] = s.plants;
      return node(only, only.name === s.name ? only.name : `${s.name} · ${only.name}`);
    }
    return { title: s.name, value: siteKey(s.id), key: siteKey(s.id), children: s.plants.map((p) => node(p)) };
  });
  const loose = (tree?.unassigned || []).map((p) => node(p));
  const children = [...sites, ...(loose.length ? [{ title: 'No site', value: UNASSIGNED_KEY, key: UNASSIGNED_KEY, children: loose }] : [])];
  return [{ title: `All plants (${readable.length})`, value: ALL_KEY, key: ALL_KEY, children }];
}

/** The plant ids the checked keys name (a site or All plants → their plants). */
function idsOfKeys(keys, data) {
  const out = new Set();
  const walk = (n, on) => {
    const hit = on || keys.includes(n.value);
    if (n.plantId != null && hit) out.add(n.plantId);
    (n.children || []).forEach((c) => walk(c, hit));
  };
  data.forEach((n) => walk(n, false));
  return [...out];
}

export default function LocationSelector({ fallbackLabel, isDark }) {
  const scope = useScope();
  const { readable, inView, everyPlant, canChoose, tree, home, byId, openRequest } = scope;
  const data = useMemo(() => treeDataOf(tree, readable), [tree, readable]);
  const current = useMemo(() => (everyPlant ? [ALL_KEY] : inView.map(plantKey)), [everyPlant, inView]);
  const [draft, setDraft] = useState(current);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (!open) setDraft(current); }, [current, open]);
  useEffect(() => { if (openRequest && canChoose) setOpen(true); }, [openRequest, canChoose]);   // a page's "Choose one plant"

  const muted = { color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)' };
  if (!canChoose) {
    const only = readable[0]?.name || fallbackLabel;
    return only ? <span className="text-sm" style={muted} data-testid="scope-label" title="The plant this session is working in">{only}</span> : null;
  }

  const commit = async () => {
    const ids = idsOfKeys(draft, data);
    const chosen = ids.length ? ids : [home ?? readable[0]?.id].filter((x) => x != null);
    const every = chosen.length === readable.length;
    const same = every ? everyPlant : (!everyPlant && chosen.length === inView.length && chosen.every((id) => inView.includes(id)));
    if (same) { setDraft(current); return; }
    try {
      await scope.choose({ plant: every ? ALL_PLANTS : chosen });
    } catch (e) {
      setDraft(current);
      toast.error(e?.response?.data?.message || 'The location choice could not be saved.');
    }
  };

  const homeName = byId[home]?.name;
  return (
    <div className="flex items-center gap-1" data-testid="scope-selector">
      <TreeSelect
        aria-label="Location"
        treeData={data}
        value={draft}
        onChange={(v) => setDraft(v)}
        treeCheckable
        showCheckedStrategy={TreeSelect.SHOW_PARENT}
        treeDefaultExpandAll
        maxTagCount="responsive"
        placeholder="Choose plants"
        suffixIcon={<MapPin size={14} aria-hidden />}
        style={{ width: 260 }}
        popupMatchSelectWidth={320}
        open={open}
        onDropdownVisibleChange={(o) => { setOpen(o); if (!o) commit(); }}
        popupClassName="scope-selector-popup"
      />
      <Tooltip title={(
        <span>
          The CPV screens show the plants chosen here; lists name each row&apos;s plant when more than one is shown.
          {' '}A new record, a sheet or a signature is made at one plant: choose that plant here first.
          {homeName ? ` Your home plant is ${homeName}.` : ''}
        </span>
      )}>
        <span role="img" aria-label="About the location" style={{ ...muted, display: 'inline-flex', cursor: 'help' }}><Info size={14} /></span>
      </Tooltip>
    </div>
  );
}

LocationSelector.propTypes = { fallbackLabel: PropTypes.string, isDark: PropTypes.bool };
